import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import AppLayout from '../components/AppLayout';
import ImportedLeaveHelpSidebar from '../components/ImportedLeaveHelpSidebar';
import { useAgentContext } from '../contexts/AgentContext';
import { useAuth } from '../contexts/AuthContext';
import { useSuccessSnackbar } from '../contexts/SuccessSnackbarContext';
import { API_ENDPOINTS } from '../constants/api';
import { apiService } from '../services/api';
import type { PagedResult } from '../types/pagedResult';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { formatDate } from '../utils/dateFormat';
import { getApiErrorMessage } from '../utils/apiError';
import {
  couldNotDelete,
  couldNotLoad,
  couldNotSave,
  genericCopy,
  pagination,
  permissionCopy,
} from '../utils/userFacingCopy';
import { deletedSuccessfully, savedSuccessfully } from '../utils/successCopy';
import {
  SortableTableHeader,
  TABLE_ACTIONS_CELL_SX,
  TABLE_BODY_ELLIPSIS_CELL_SX,
  TABLE_DATE_CELL_SX,
  TABLE_HEAD_NOWRAP_SX,
} from '../utils/sortableTableHeader';
import {
  balanceRowKey,
  isValidGuid,
  mapImportedLeaveBalance,
  type ImportedLeaveBalance,
  type ImportedLeaveBalanceApi,
} from '../utils/importedLeaveBalance';

interface LeaveTypeOption {
  id: string;
  code: string;
  name: string;
}

const IMPORTED_LEAVE_COLUMN_WIDTHS = {
  employee: '16%',
  department: '12%',
  leaveType: '19%',
  year: '6%',
  month: '7%',
  daysTaken: '9%',
  lastUpdated: '23%',
  actions: '8%',
} as const;

const IMPORTED_LEAVE_HEAD_CELL_SX = (width: string) => ({ width, ...TABLE_HEAD_NOWRAP_SX });

type SortableColumn =
  | 'employeeName'
  | 'leaveTypeName'
  | 'periodYear'
  | 'month'
  | 'daysTaken'
  | 'updatedAtUtc';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = ['all', String(CURRENT_YEAR - 1), String(CURRENT_YEAR), String(CURRENT_YEAR + 1)];

interface EditLeaveBalanceDialogProps {
  open: boolean;
  balance: ImportedLeaveBalance | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

/** Isolated dialog so typing days taken does not re-render the full balances table. */
function EditLeaveBalanceDialog({ open, balance, onClose, onSaved }: EditLeaveBalanceDialogProps) {
  const showSuccess = useSuccessSnackbar();
  const [daysTakenInput, setDaysTakenInput] = useState('');
  const [editError, setEditError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && balance) {
      setDaysTakenInput(String(balance.daysTaken));
      setEditError('');
    }
  }, [open, balance]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSave = async () => {
    if (!balance) return;
    const days = parseFloat(daysTakenInput);
    if (Number.isNaN(days) || days <= 0) {
      setEditError('Enter days taken greater than zero.');
      return;
    }

    setSubmitting(true);
    setEditError('');
    try {
      await apiService.put(API_ENDPOINTS.LEAVE.BALANCE_BY_ID(balance.id), { daysTaken: days });
      showSuccess(savedSuccessfully('Leave balance'));
      onClose();
      await onSaved();
    } catch (err: unknown) {
      setEditError(
        err instanceof Error && !('response' in (err as object))
          ? err.message
          : getApiErrorMessage(err, couldNotSave('leave balance'))
      );
    } finally {
      setSubmitting(false);
    }
  };

  const periodLabel = balance
    ? balance.monthLabel
      ? `${balance.monthLabel} ${balance.periodYear}`
      : String(balance.periodYear)
    : '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit days taken</DialogTitle>
      <DialogContent>
        {balance && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {balance.employeeName}
          </Typography>
        )}
        {editError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {editError}
          </Alert>
        )}
        {balance && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Leave type:</strong> {balance.leaveTypeDisplayName}
              <br />
              <strong>Period:</strong> {periodLabel}
            </Typography>
            <TextField
              label="Days taken"
              type="number"
              fullWidth
              margin="dense"
              autoFocus
              inputProps={{ min: 0.5, step: 0.5 }}
              value={daysTakenInput}
              onChange={(e) => setDaysTakenInput(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleSave()}
          variant="contained"
          disabled={submitting || !balance}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ImportedLeaveBalancesPage() {
  const { user } = useAuth();
  const { showLegacyHelp } = useAgentContext();
  const showSuccess = useSuccessSnackbar();
  const isAdmin = user?.roles.includes('Administrator') ?? false;

  const [balances, setBalances] = useState<ImportedLeaveBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [periodYearFilter, setPeriodYearFilter] = useState('all');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pagination.auditLogDefaultPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<SortableColumn>('updatedAtUtc');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeOption[]>([]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [balanceToEdit, setBalanceToEdit] = useState<ImportedLeaveBalance | null>(null);
  const [balanceToDelete, setBalanceToDelete] = useState<ImportedLeaveBalance | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [helpExpanded, setHelpExpanded] = useState(false);

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setBalanceToDelete(null);
    setDeleteError('');
  };

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => window.clearTimeout(handle);
  }, [searchQuery]);

  useEffect(() => {
    if (!isAdmin) return;
    const loadLookups = async () => {
      try {
        const typesRes = await apiService.get<LeaveTypeOption[]>(API_ENDPOINTS.LEAVE_REQUESTS.LEAVE_TYPES);
        setLeaveTypes(
          typesRes.data.map((t) => ({
            id: t.id,
            code: t.code,
            name: t.name,
          }))
        );
      } catch {
        // Lookups optional — used for leave type filter only
      }
    };
    void loadLookups();
  }, [isAdmin]);

  const loadBalances = useCallback(async () => {
    if (!isAdmin) {
      setError(permissionCopy.administrator);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('pageSize', String(rowsPerPage));
      params.append('sortBy', sortBy);
      params.append('sortDirection', sortDirection);
      if (debouncedSearch) params.append('searchQuery', debouncedSearch);
      if (periodYearFilter !== 'all') params.append('periodYear', periodYearFilter);
      if (leaveTypeFilter !== 'all') params.append('leaveTypeName', leaveTypeFilter);

      const response = await apiService.get<PagedResult<ImportedLeaveBalanceApi>>(
        `${API_ENDPOINTS.LEAVE.BALANCES}?${params.toString()}`
      );
      const rawItems =
        response.data.items ??
        (response.data as { Items?: ImportedLeaveBalanceApi[] }).Items ??
        [];
      const mapped = rawItems
        .map(mapImportedLeaveBalance)
        .filter((row): row is ImportedLeaveBalance => row != null);
      if (mapped.length !== rawItems.length) {
        setError(genericCopy.somethingWrong);
      }
      setBalances(mapped);
      setTotalCount(response.data.totalCount);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, couldNotLoad('imported leave balances')));
    } finally {
      setLoading(false);
    }
  }, [
    isAdmin,
    page,
    rowsPerPage,
    debouncedSearch,
    periodYearFilter,
    leaveTypeFilter,
    sortBy,
    sortDirection,
  ]);

  useEffect(() => {
    void loadBalances();
  }, [loadBalances]);

  const handleSort = (column: SortableColumn) => {
    if (sortBy === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const handleEditClick = (row: ImportedLeaveBalance) => {
    setBalanceToEdit(row);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (row: ImportedLeaveBalance) => {
    if (!isValidGuid(row.id)) {
      setError(genericCopy.somethingWrong);
      return;
    }
    setBalanceToDelete(row);
    setDeleteError('');
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!balanceToDelete) return;

    try {
      setDeleting(true);
      setDeleteError('');
      await apiService.delete(API_ENDPOINTS.LEAVE.BALANCE_BY_ID(balanceToDelete.id));
      showSuccess(deletedSuccessfully('Leave balance'));
      closeDeleteDialog();
      await loadBalances();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setDeleteError(couldNotDelete('leave balance'));
        closeDeleteDialog();
        await loadBalances();
        return;
      }
      setDeleteError(getApiErrorMessage(err, couldNotDelete('leave balance')));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <EventNoteIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            Imported Leave Balances
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" component="span">
            Edit incorrect <strong>days taken</strong> on imported rows, or delete wrong rows. You cannot add rows — use{' '}
            <strong>Book leave on behalf</strong> on Organisation Leave or ask the employee to apply on My Leave.
            Changes are on the <strong>Leave audit log</strong>.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }} aria-busy="true">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <TextField
                    sx={{ flex: 1, minWidth: 200 }}
                    label="Search imported leave"
                    placeholder="Search by employee name or number..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setPage(0);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Clear search"
                            onClick={() => setSearchQuery('')}
                            edge="end"
                            sx={{ mr: -1 }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                  <FormControl sx={{ minWidth: 160 }}>
                    <InputLabel id="filter-year-label">Period year</InputLabel>
                    <Select
                      labelId="filter-year-label"
                      label="Period year"
                      value={periodYearFilter}
                      onChange={(e) => {
                        setPeriodYearFilter(e.target.value);
                        setPage(0);
                      }}
                    >
                      <MenuItem value="all">All years</MenuItem>
                      {YEAR_OPTIONS.filter((y) => y !== 'all').map((y) => (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="filter-type-label">Leave type</InputLabel>
                    <Select
                      labelId="filter-type-label"
                      label="Leave type"
                      value={leaveTypeFilter}
                      onChange={(e) => {
                        setLeaveTypeFilter(e.target.value);
                        setPage(0);
                      }}
                    >
                      <MenuItem value="all">All types</MenuItem>
                      {leaveTypes.map((t) => (
                        <MenuItem key={t.code} value={t.code}>
                          {t.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {(debouncedSearch || periodYearFilter !== 'all' || leaveTypeFilter !== 'all') && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                      mt: 2,
                      pt: 2,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {debouncedSearch && (
                      <Chip
                        label={`Search: "${debouncedSearch}"`}
                        onDelete={() => setSearchQuery('')}
                        size="small"
                        color="primary"
                      />
                    )}
                    {periodYearFilter !== 'all' && (
                      <Chip
                        label={`Period year: ${periodYearFilter}`}
                        onDelete={() => setPeriodYearFilter('all')}
                        size="small"
                        color="info"
                      />
                    )}
                    {leaveTypeFilter !== 'all' && (
                      <Chip
                        label={`Leave type: ${leaveTypes.find((t) => t.code === leaveTypeFilter)?.name ?? leaveTypeFilter}`}
                        onDelete={() => setLeaveTypeFilter('all')}
                        size="small"
                        color="info"
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2 }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <TableContainer sx={{ borderTop: 0 }}>
                  <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={IMPORTED_LEAVE_HEAD_CELL_SX(IMPORTED_LEAVE_COLUMN_WIDTHS.employee)}>
                          <SortableTableHeader
                            label="Employee"
                            active={sortBy === 'employeeName'}
                            direction={sortBy === 'employeeName' ? sortDirection : 'asc'}
                            onClick={() => handleSort('employeeName')}
                          />
                        </TableCell>
                        <TableCell sx={IMPORTED_LEAVE_HEAD_CELL_SX(IMPORTED_LEAVE_COLUMN_WIDTHS.department)}>
                          <strong>Department</strong>
                        </TableCell>
                        <TableCell sx={IMPORTED_LEAVE_HEAD_CELL_SX(IMPORTED_LEAVE_COLUMN_WIDTHS.leaveType)}>
                          <SortableTableHeader
                            label="Leave type"
                            active={sortBy === 'leaveTypeName'}
                            direction={sortBy === 'leaveTypeName' ? sortDirection : 'asc'}
                            onClick={() => handleSort('leaveTypeName')}
                          />
                        </TableCell>
                        <TableCell sx={IMPORTED_LEAVE_HEAD_CELL_SX(IMPORTED_LEAVE_COLUMN_WIDTHS.year)}>
                          <SortableTableHeader
                            label="Year"
                            active={sortBy === 'periodYear'}
                            direction={sortBy === 'periodYear' ? sortDirection : 'asc'}
                            onClick={() => handleSort('periodYear')}
                          />
                        </TableCell>
                        <TableCell sx={IMPORTED_LEAVE_HEAD_CELL_SX(IMPORTED_LEAVE_COLUMN_WIDTHS.month)}>
                          <SortableTableHeader
                            label="Month"
                            active={sortBy === 'month'}
                            direction={sortBy === 'month' ? sortDirection : 'asc'}
                            onClick={() => handleSort('month')}
                          />
                        </TableCell>
                        <TableCell align="right" sx={IMPORTED_LEAVE_HEAD_CELL_SX(IMPORTED_LEAVE_COLUMN_WIDTHS.daysTaken)}>
                          <SortableTableHeader
                            label="Days taken"
                            active={sortBy === 'daysTaken'}
                            direction={sortBy === 'daysTaken' ? sortDirection : 'asc'}
                            onClick={() => handleSort('daysTaken')}
                            alignRight
                          />
                        </TableCell>
                        <TableCell sx={IMPORTED_LEAVE_HEAD_CELL_SX(IMPORTED_LEAVE_COLUMN_WIDTHS.lastUpdated)}>
                          <SortableTableHeader
                            label="Last updated"
                            active={sortBy === 'updatedAtUtc'}
                            direction={sortBy === 'updatedAtUtc' ? sortDirection : 'asc'}
                            onClick={() => handleSort('updatedAtUtc')}
                          />
                        </TableCell>
                        <TableCell align="right" sx={IMPORTED_LEAVE_HEAD_CELL_SX(IMPORTED_LEAVE_COLUMN_WIDTHS.actions)}>
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {balances.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4, border: 'none' }}>
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                              <EventNoteIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                              <Typography variant="h6" color="text.secondary" gutterBottom>
                                {debouncedSearch || periodYearFilter !== 'all' || leaveTypeFilter !== 'all'
                                  ? 'No balances match your filters'
                                  : 'No imported leave balances yet'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {debouncedSearch || periodYearFilter !== 'all' || leaveTypeFilter !== 'all'
                                  ? 'Try adjusting your search or filter criteria.'
                                  : 'Balances from spreadsheet import will appear here when present.'}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        balances.map((row) => {
                          const employeeLabel =
                            row.employeeNumber != null
                              ? `${row.employeeName} (#${row.employeeNumber})`
                              : row.employeeName;
                          const departmentLabel = row.departmentName ?? '—';
                          const leaveTypeLabel = row.leaveTypeDisplayName;
                          const updatedLabel = formatDate(row.updatedAtUtc, { includeTime: true });

                          return (
                          <TableRow key={balanceRowKey(row)}>
                            <TableCell sx={TABLE_BODY_ELLIPSIS_CELL_SX} title={employeeLabel}>
                              {employeeLabel}
                            </TableCell>
                            <TableCell sx={TABLE_BODY_ELLIPSIS_CELL_SX} title={departmentLabel}>
                              {departmentLabel}
                            </TableCell>
                            <TableCell sx={TABLE_BODY_ELLIPSIS_CELL_SX} title={leaveTypeLabel}>
                              {leaveTypeLabel}
                            </TableCell>
                            <TableCell sx={TABLE_DATE_CELL_SX}>{row.periodYear}</TableCell>
                            <TableCell sx={TABLE_DATE_CELL_SX}>{row.monthLabel ?? '—'}</TableCell>
                            <TableCell align="right" sx={TABLE_DATE_CELL_SX}>{row.daysTaken}</TableCell>
                            <TableCell sx={TABLE_DATE_CELL_SX} title={updatedLabel}>
                              {updatedLabel}
                            </TableCell>
                            <TableCell align="right" sx={TABLE_ACTIONS_CELL_SX}>
                              <Box sx={{ display: 'inline-flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                <IconButton
                                  size="small"
                                  aria-label={`Edit balance for ${row.employeeName}`}
                                  onClick={() => handleEditClick(row)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  aria-label={`Delete balance for ${row.employeeName}`}
                                  onClick={() => handleDeleteClick(row)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[...pagination.auditLogPageSizeOptions]}
                  sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
                />
              </CardContent>
            </Card>
          </>
        )}

        <EditLeaveBalanceDialog
          open={editDialogOpen}
          balance={balanceToEdit}
          onClose={() => {
            setEditDialogOpen(false);
            setBalanceToEdit(null);
          }}
          onSaved={loadBalances}
        />

        <Dialog
          open={deleteDialogOpen}
          onClose={() => {
            if (!deleting) {
              closeDeleteDialog();
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete imported leave balance?</DialogTitle>
          <DialogContent>
            {deleteError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {deleteError}
              </Alert>
            )}
            {balanceToDelete && (
              <Typography>
                Remove <strong>{balanceToDelete.daysTaken}</strong> day{balanceToDelete.daysTaken !== 1 ? 's' : ''} of{' '}
                <strong>{balanceToDelete.leaveTypeDisplayName}</strong> for <strong>{balanceToDelete.employeeName}</strong>
                {balanceToDelete.monthLabel
                  ? ` (${balanceToDelete.monthLabel} ${balanceToDelete.periodYear})`
                  : ` (${balanceToDelete.periodYear})`}
                ? This updates leave reports immediately.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog} disabled={deleting} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button onClick={() => void handleDelete()} color="error" variant="contained" disabled={deleting} sx={{ textTransform: 'none' }}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      {showLegacyHelp && (
        <ImportedLeaveHelpSidebar
          isExpanded={helpExpanded}
          onToggle={() => setHelpExpanded(!helpExpanded)}
        />
      )}
    </AppLayout>
  );
}
